import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@material-ui/core'
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf,
  Description as DescriptionIcon,
  InsertDriveFile,
  Image as ImageIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  ErrorOutline
} from '@material-ui/icons'

const ACCEPT = '.pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif'

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const getFileTypeFromName = (name) => {
  if (!name) return 'other'
  const lower = name.toLowerCase()
  if (lower.endsWith('.pdf')) return 'pdf'
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'excel'
  if (lower.endsWith('.csv')) return 'csv'
  if (lower.match(/\.(jpe?g|png|gif)$/)) return 'image'
  return 'other'
}

const iconForType = (type, style = {}) => {
  switch (type) {
    case 'pdf':
      return <PictureAsPdf style={{ color: '#d32f2f', ...style }} />
    case 'excel':
    case 'csv':
      return <DescriptionIcon style={{ color: '#2e7d32', ...style }} />
    case 'image':
      return <ImageIcon style={{ color: '#1976d2', ...style }} />
    default:
      return <InsertDriveFile style={{ color: '#757575', ...style }} />
  }
}

const DocumentUploadModal = ({ open, onClose, onUploaded, projectId, url }) => {
  const fileInputRef = React.useRef(null)
  const [selectedFile, setSelectedFile] = React.useState(null)
  const [uploading, setUploading] = React.useState(false)
  const [progress, setProgress] = React.useState(0)
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState(null)

  const reset = () => {
    setSelectedFile(null)
    setUploading(false)
    setProgress(0)
    setError('')
    setResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    if (uploading) return
    reset()
    onClose()
  }

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0]
    if (f) {
      setSelectedFile(f)
      setError('')
      setResult(null)
    }
  }

  const handleUpload = () => {
    if (!selectedFile) {
      setError('Please select a file to upload.')
      return
    }
    setUploading(true)
    setError('')
    setProgress(0)

    const formData = new FormData()
    formData.append('file', selectedFile)

    axios.post(`${url}/api/bid-estimation/projects/${projectId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total) {
          setProgress(Math.round((e.loaded / e.total) * 100))
        }
      }
    }).then(res => {
      setUploading(false)
      setResult(res.data)
      if (onUploaded) onUploaded(res.data)
    }).catch(err => {
      setUploading(false)
      setError(err.response?.data?.error || err.response?.data?.message || 'Upload failed. Please try again.')
    })
  }

  const handleDone = () => {
    reset()
    onClose()
  }

  const renderParseMessage = () => {
    if (!result) return null
    const status = result.parseStatus
    if (status === 'classified') {
      const pr = result.parseResult || {}
      const pageCount = pr.pageCount || 0
      const schedCount = (pr.sheetIndex || []).filter(s => s.hasSchedule).length
      const classifyTime = pr.classifyTime
      return (
        <div style={{
          marginTop: 16,
          padding: '12px 16px',
          backgroundColor: '#e3f2fd',
          borderRadius: 4,
          borderLeft: '4px solid #1565c0',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <CheckIcon style={{ color: '#1565c0' }} />
          <Typography variant="body2">
            Plan set detected — {pageCount} pages classified{classifyTime ? ` in ${classifyTime}s` : ''}.
            {schedCount > 0 ? ` ${schedCount} page${schedCount !== 1 ? 's' : ''} with extractable schedules.` : ''}
            {' '}Close this dialog and click <strong>"Select Pages"</strong> to choose which to extract.
          </Typography>
        </div>
      )
    }
    if (status === 'parsed') {
      return (
        <div style={{
          marginTop: 16,
          padding: '12px 16px',
          backgroundColor: '#e8f5e9',
          borderRadius: 4,
          borderLeft: '4px solid #2e7d32',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <CheckIcon style={{ color: '#2e7d32' }} />
          <Typography variant="body2">
            Document parsed! Click "Import Items" to add them to the project.
          </Typography>
        </div>
      )
    }
    if (status === 'failed') {
      return (
        <div style={{
          marginTop: 16,
          padding: '12px 16px',
          backgroundColor: '#ffebee',
          borderRadius: 4,
          borderLeft: '4px solid #c62828',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <ErrorOutline style={{ color: '#c62828' }} />
          <Typography variant="body2">
            File uploaded but couldn't be parsed — you can still view it in S3.
          </Typography>
        </div>
      )
    }
    if (status === 'unsupported') {
      return (
        <div style={{
          marginTop: 16,
          padding: '12px 16px',
          backgroundColor: '#fff3e0',
          borderRadius: 4,
          borderLeft: '4px solid #ef6c00',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <WarningIcon style={{ color: '#ef6c00' }} />
          <Typography variant="body2">
            File uploaded for reference.
          </Typography>
        </div>
      )
    }
    return (
      <div style={{
        marginTop: 16,
        padding: '12px 16px',
        backgroundColor: '#f5f5f5',
        borderRadius: 4
      }}>
        <Typography variant="body2">
          File uploaded successfully.
        </Typography>
      </div>
    )
  }

  const displayType = selectedFile ? getFileTypeFromName(selectedFile.name) : 'other'

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload Document</Typography>
          <IconButton size="small" onClick={handleClose} disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Supported formats: PDF, Excel (.xlsx, .xls), CSV, Images (.jpg, .png, .gif)
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {!selectedFile && (
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
            style={{ padding: 24, textTransform: 'none' }}
          >
            Choose a file…
          </Button>
        )}

        {selectedFile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            backgroundColor: '#fafafa'
          }}>
            {iconForType(displayType, { fontSize: 36 })}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatBytes(selectedFile.size)}
              </Typography>
            </div>
            {!uploading && !result && (
              <Button
                size="small"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                style={{ textTransform: 'none' }}
              >
                Change
              </Button>
            )}
          </div>
        )}

        {uploading && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Typography variant="caption">Uploading…</Typography>
              <Typography variant="caption">{progress}%</Typography>
            </div>
            <LinearProgress variant="determinate" value={progress} />
          </div>
        )}

        {error && (
          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            backgroundColor: '#ffebee',
            borderRadius: 4,
            borderLeft: '4px solid #c62828',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <ErrorOutline style={{ color: '#c62828' }} />
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </div>
        )}

        {renderParseMessage()}
      </DialogContent>

      <DialogActions>
        {!result && (
          <>
            <Button onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              color="primary"
              disabled={uploading || !selectedFile}
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        )}
        {result && (
          <Button onClick={handleDone} variant="contained" color="primary">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default DocumentUploadModal
